import React from 'react';
import * as Headless from '@headlessui/react';
import { Form, useNavigate } from 'react-router-dom';
import {
  DocumentMagnifyingGlassIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@oysterjs/ui/dialog';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Spinner } from '@oysterjs/ui/spinner';

import { FileInput } from '~/components/file';
import { client } from '~/api/client';
import {
  CaraWorkspaceFileInput,
  CreateWorkflowExecutionDocument,
  CreateWorkflowExecutionMutation,
  WorkflowInputType
} from '~/api/graphql';
import { ApolloError } from '@apollo/client';

type WorkflowTile = {
  id: string;
  title: string;
  description: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  category: string;
  categoryColor: React.ComponentProps<typeof Badge>['color'];
  definitionId: string;
  definitionVersion: number;
};

const workflowTiles: WorkflowTile[] = [
  {
    id: 'coverage-comparison',
    title: 'Coverage Comparison',
    description:
      'Compare two or more insurance policies side by side. Analyzes coverages, forms, claims procedures, and identifies gaps and differences.',
    icon: DocumentMagnifyingGlassIcon,
    category: 'Analysis',
    categoryColor: 'sky',
    definitionId: 'default',
    definitionVersion: 1
  }
];

export const WorkflowGalleryPage = () => {
  const [activeTile, setActiveTile] = React.useState<WorkflowTile | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const openTile = (tile: WorkflowTile) => {
    setActiveTile(tile);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {workflowTiles.map((tile) => (
          <button
            onClick={() => openTile(tile)}
            className="group relative flex flex-col text-left w-full rounded-lg bg-white dark:bg-neutral-800 shadow hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            {/* Gradient accent bar */}
            {/* <div className="h-1 w-full bg-gradient-to-r from-primary-400 via-primary-500 to-sky-400 opacity-80 group-hover:opacity-100 transition-opacity" /> */}

            {/* Icon stage */}
            <div className="px-5 pt-5 pb-3">
              <div className="inline-flex rounded-xl bg-gradient-to-br from-primary-50 to-sky-50 dark:from-primary-900/30 dark:to-sky-900/20 p-3.5 ring-1 ring-primary-100/60 dark:ring-primary-800/30">
                <tile.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>

            {/* Content */}
            <div className="px-5 pb-5 flex flex-col flex-1">
              <h3 className="text-base/6 font-heading m-0 p-0 font-semibold text-neutral-900 dark:text-white">
                {tile.title}
              </h3>

              <Badge color={tile.categoryColor} className="self-start mt-1.5 mb-2.5">
                {tile.category}
              </Badge>

              <p className="text-sm/5 text-neutral-500 dark:text-neutral-400 line-clamp-3">
                {tile.description}
              </p>

              {/* CTA hint */}
              <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-700/50 flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400">
                <span>Launch workflow</span>
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </div>
            </div>
          </button>
        ))}
      </div>

      <StartWorkflowDialog tile={activeTile} open={dialogOpen} onClose={closeDialog} />
    </>
  );
};

type ComparisonOption = {
  id: string;
  label: string;
  files: CaraWorkspaceFileInput[];
};

const makeOption = (index: number): ComparisonOption => ({
  id: crypto.randomUUID(),
  label: `Option ${index + 1}`,
  files: []
});

const StartWorkflowDialog = (props: {
  tile: WorkflowTile | null;
  open: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [options, setOptions] = React.useState<ComparisonOption[]>([]);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string>();

  React.useEffect(() => {
    if (props.open) {
      setOptions([makeOption(0), makeOption(1)]);
      setCreating(false);
      setError(undefined);
    }
  }, [props.open]);

  const addOption = () => {
    setOptions((prev) => [...prev, makeOption(prev.length)]);
  };

  const removeOption = (optionId: string) => {
    setOptions((prev) => prev.filter((o) => o.id !== optionId));
  };

  const updateOptionFiles = (optionId: string, files: CaraWorkspaceFileInput[]) => {
    setOptions((prev) => prev.map((o) => (o.id === optionId ? { ...o, files } : o)));
  };

  const validOptions = options.filter((o) => o.files.length > 0);
  const canStart = validOptions.length >= 2 && !creating;

  const handleStart = async () => {
    if (validOptions.length < 2) {
      setError('Please add files for at least two options to compare.');
      return;
    }

    setCreating(true);
    setError(undefined);

    try {
      const inputs = options.flatMap((option) =>
        option.files.map((f) => ({
          type: WorkflowInputType.File,
          label: option.label,
          enumerable: true,
          file: f
        }))
      );

      const res = await client.mutate<CreateWorkflowExecutionMutation>({
        mutation: CreateWorkflowExecutionDocument,
        variables: {
          input: {
            definitionId: props.tile!.definitionId,
            definitionVersion: props.tile!.definitionVersion,
            inputs
          }
        }
      });

      if (res.errors?.length) {
        throw new ApolloError({ graphQLErrors: res.errors });
      }

      const exec = res.data?.createWorkflowExecution;
      if (exec) {
        props.onClose();
        navigate(`/app/workflows/executions/${exec.id}`);
      }
    } catch (e) {
      setError(e instanceof ApolloError ? e.message : 'Failed to start workflow');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} size="xl">
      <DialogTitle>{props.tile?.title}</DialogTitle>
      <DialogDescription>
        Add the policy documents for each option you want to compare. Each option can contain
        multiple files (e.g. declarations page, endorsements, schedules).
      </DialogDescription>
      <DialogBody>
        <Form>
          <Fieldset>
            <FieldGroup>
              {options.map((option, index) => (
                <Field key={option.id}>
                  <Label>{option.label}</Label>
                  {index > 1 && (
                    <Headless.Button
                      type="button"
                      onClick={() => removeOption(option.id)}
                      className="ml-1.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 text-xs font-medium align-middle"
                      aria-label={`Remove ${option.label}`}
                    >
                      <XMarkIcon className="size-3" />
                    </Headless.Button>
                  )}
                  <FileInput
                    onChange={(change) => updateOptionFiles(option.id, change.files)}
                    multiple
                    enableExisting
                  />
                </Field>
              ))}
            </FieldGroup>
          </Fieldset>
        </Form>

        <Button plain onClick={addOption} className="mt-6 w-full">
          + Add another option
        </Button>

        {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" onClick={props.onClose} disabled={creating}>
          Cancel
        </Button>
        <Button type="button" color="sky" onClick={handleStart} disabled={!canStart}>
          {creating ? (
            <span className="flex items-center gap-2">
              <Spinner size="size-4" color="white" /> Starting…
            </span>
          ) : (
            'Start Comparison'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
